import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'primereact/dropdown'
import { Calendar } from 'primereact/calendar'
import Loader from 'Components/Loader'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { commonMethods } from 'Utils/commonMethods'

const marketTypes = [
  { name: 'Robusta', idMarket: 1 },
  { name: 'Arabica', idMarket: 2 }
]

const EditHoliday = ({ setLoading, robustaHolidayList, arabicaHolidayList, setRobustaHolidayList, setArabicaHolidayList, mockEditHoliday }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const searchParams = new URLSearchParams(location.search)
  const currentTab = searchParams.get('tab')
  const idHoliday = searchParams.get('edit-holiday')
  const { utcToLocalTime, formatDateToISOWithoutTimezone } = commonMethods
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })

  const [selectedMarketType, setSelectedMarketType] = useState(null)
  const [marketStartDateAndTime, setMarketStartDateAndTime] = useState(null)
  const [marketEndDateAndTime, setMarketEndDateAndTime] = useState(null)
  const [editHolidayDetails, setEditHolidayDetails] = useState({})

  useEffect(() => {
    const holidayList = currentTab === 'robusta' ? robustaHolidayList : arabicaHolidayList

    if (holidayList.length > 0) {
      const selectedHoliday = holidayList?.find((ele) => ele?.idHoliday === Number(idHoliday))
      const selectedMarketType = marketTypes.find((ele) => ele?.name === selectedHoliday?.market)
      const holidayStartDateTime = new Date(selectedHoliday?.marketStartDateTime)
      const holidayEndDateTime = new Date(selectedHoliday?.marketEndDateTime)

      setSelectedMarketType(selectedMarketType)
      setMarketStartDateAndTime(holidayStartDateTime)
      setMarketEndDateAndTime(holidayEndDateTime)
      setEditHolidayDetails(selectedHoliday)
    }
  }, [robustaHolidayList])

  // useEffect to initialize state for testing purpose
  useEffect(() => {
    if (mockEditHoliday) {
      handleClickSaveButton()
    }
  }, [])

  const handleMarketTypeDropdownChange = (e) => {
    setEditHolidayDetails({ ...editHolidayDetails, idMarket: e.value.idMarket, market: e.value.name })
    setSelectedMarketType(e.value)
  }

  const handleChangeMarketStartDateAndTime = (e) => {
    const isoTime = formatDateToISOWithoutTimezone(e.value)
    const formattedDate = utcToLocalTime(isoTime)

    setEditHolidayDetails({ ...editHolidayDetails, marketStartDateTime: isoTime, formattedMarketStartDateTime: formattedDate })
    setMarketStartDateAndTime(e.value)
  }

  const handleChangeMarketEndDateAndTime = (e) => {
    const isoTime = formatDateToISOWithoutTimezone(e.value)
    const formattedDate = utcToLocalTime(isoTime)

    setEditHolidayDetails({ ...editHolidayDetails, marketEndDateTime: isoTime, formattedMarketEndDateTime: formattedDate })
    setMarketEndDateAndTime(e.value)
  }

  const handleClickSaveButton = async () => {
    const { formattedMarketStartDateTime, formattedMarketEndDateTime, ...postData } = editHolidayDetails

    setLoading(true)
    try {
      const res = await apiAdapterCoffeeWeb.editHolidayData(postData)

      if (res?.data?.isSuccess) {
        const holidayList = currentTab === 'robusta' ? robustaHolidayList : arabicaHolidayList

        const modifiedData = holidayList?.map((ele) => {
          if (ele.idHoliday === editHolidayDetails.idHoliday) {
            return editHolidayDetails
          }

          return ele
        })

        if (currentTab === 'robusta') {
          setRobustaHolidayList(modifiedData)
        } else {
          setArabicaHolidayList(modifiedData)
        }
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('HOLIDAY_DATA_UPDATED_SUCCESSFULLY'),
          onHide: handleSuccess,
          onSuccess: handleSuccess
        })
      }
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: error.response.data.message,
        onHide: handleDismiss,
        onRetry: handleClickSaveButton
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSuccess = () => {
    setShowDialog({ ...showDialog, showModal: false })
    searchParams.delete('edit-holiday')
    history.goBack({ pathname: ROUTE_STRINGS.listHoliday, search: 'tab=robusta' })
  }

  const handleDismiss = () => setShowDialog({ ...showDialog, showModal: false })
  const isSaveButtonEnabled = selectedMarketType && marketStartDateAndTime && marketEndDateAndTime

  return (
    <div className="edit-holiday-component-wrapper" data-testid="edit-holiday-component">
      <div className="edit-holiday-form">
        <div className="p-float-label">
          <Dropdown value={selectedMarketType} onChange={(e) => handleMarketTypeDropdownChange(e)} options={marketTypes} optionLabel="name" className="w-full md:w-14rem" disabled data-testid="market-type-dropdown" />
          <label htmlFor="menuPath">
            <span>{t('MARKET_TYPE')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <Calendar id="calendar-24h" value={marketStartDateAndTime} onChange={(e) => handleChangeMarketStartDateAndTime(e)} showTime hourFormat="24" data-testid="start-calender" />
          <label htmlFor="menuPath">
            <span>{t('STARTS_FROM')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <Calendar id="calendar-24h" value={marketEndDateAndTime} onChange={(e) => handleChangeMarketEndDateAndTime(e)} showTime hourFormat="24" data-testid="end-calender" />
          <label htmlFor="menuPath">
            <span>{t('ENDS_AT')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="edit-holiday-form-footer">
          <ButtonComponent type={'submit'} onClick={handleClickSaveButton} disabled={!isSaveButtonEnabled} />
        </div>
      </div>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </div>
  )
}

export default Loader(EditHoliday)
